import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { auth } from '@/api/endpoints';
import { setApiAppClient } from '@/api/client';
import {
  clearSession,
  detectAppMode,
  getCachedUser,
  getToken,
  isAuthFailure,
  setCachedUser,
  type AppClient,
  type SessionUser,
} from '@/auth/session';
import { AppShell } from '@/components/Shell';
import { Loading } from '@/components/ui';
import {
  adminPathForScreen,
  adminScreenFromPath,
  ADMIN_DEFAULT_PATH,
  ADMIN_LOGIN_PATH,
} from '@/nav/adminRoutes';
import {
  customerPathForScreen,
  customerScreenFromPath,
  isCustomerAuthScreen,
  CUSTOMER_DEFAULT_PATH,
  CUSTOMER_LOGIN_PATH,
  CUSTOMER_REGISTER_PATH,
} from '@/nav/customerRoutes';
import { customerWalletMode, type AdminScreenKey, type CustomerScreenKey } from '@/nav/keys';

import { LoginScreen } from '@/screens/customer/Login';
import { RegisterScreen } from '@/screens/customer/Register';
import { HomeScreen } from '@/screens/customer/Home';
import { BillsScreen } from '@/screens/customer/Bills';
import { ProductsScreen } from '@/screens/customer/Products';
import { WalletScreen } from '@/screens/customer/Wallet';
import { OrdersScreen } from '@/screens/customer/Orders';
import { SupportScreen } from '@/screens/customer/Support';
import { KycScreen } from '@/screens/customer/Kyc';
import { NotificationsScreen } from '@/screens/customer/Notifications';
import { SessionsScreen } from '@/screens/customer/Sessions';
import { AccountScreen } from '@/screens/customer/Account';
import { PartnerApiScreen } from '@/screens/customer/PartnerApi';
import { QuickPayScreen } from '@/screens/customer/QuickPay';
import { DistributorScreen } from '@/screens/customer/Distributor';
import { CustomersScreen } from '@/screens/customer/Customers';
import { DebtsScreen } from '@/screens/customer/Debts';
import { ReportsScreen } from '@/screens/customer/Reports';
import { MoreScreen } from '@/screens/customer/More';

import { AdminLoginScreen } from '@/screens/admin/Login';
import { DashboardScreen } from '@/screens/admin/Dashboard';
import { UsersScreen } from '@/screens/admin/Users';
import { DepositsScreen } from '@/screens/admin/Deposits';
import { PaymentsScreen } from '@/screens/admin/Payments';
import { UnitsOrdersScreen, GamesOrdersScreen } from '@/screens/admin/Orders';
import {
  ProductsLocalScreen,
  CategoriesScreen,
  OrderingScreen,
  ImportScreen,
  SourcesScreen,
} from '@/screens/admin/Products';
import { RechargePackagesScreen, ServiceControlsScreen } from '@/screens/admin/Recharge';
import { ShamBalanceScreen } from '@/screens/admin/ShamBalance';
import { ExecutorScreen } from '@/screens/admin/Executor';
import { UssdScreen } from '@/screens/admin/Ussd';
import { BillsAdminScreen } from '@/screens/admin/Bills';
import { MembershipsScreen } from '@/screens/admin/Memberships';
import { SiteSettingsScreen, SlidesScreen } from '@/screens/admin/SiteSettings';
import { FinancialScreen } from '@/screens/admin/Financial';
import { SecurityAuditScreen } from '@/screens/admin/SecurityAudit';
import { ComplianceScreen } from '@/screens/admin/Compliance';
import { StaffScreen } from '@/screens/admin/Staff';
import { SmsMessagesScreen } from '@/screens/admin/Sms';
import { AdminPasswordScreen } from '@/screens/admin/Password';

export type AppProps = {
  mode?: AppClient;
};

export function App({ mode: modeProp }: AppProps = {}) {
  const mode: AppClient = modeProp ?? detectAppMode();

  if (mode === 'admin') {
    return <AdminApp />;
  }

  return <CustomerApp />;
}

function useSessionBoot(mode: AppClient) {
  const [booting, setBooting] = useState(true);
  const [user, setUser] = useState<SessionUser | null>(() => getCachedUser(mode));

  useEffect(() => {
    setApiAppClient(mode);
  }, [mode]);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      setApiAppClient(mode);
      const token = getToken(mode);
      if (!token) {
        if (!cancelled) {
          setUser(null);
          setBooting(false);
        }
        return;
      }
      const res = await auth.me();
      if (cancelled) return;
      if (!res.success || !res.user || typeof res.user !== 'object') {
        // Only wipe the session on real auth rejection. Network/5xx must not
        // force logout — that was logging admins out during deploys/errors.
        if (isAuthFailure(res)) {
          clearSession(mode);
          setUser(null);
        } else {
          const cached = getCachedUser(mode);
          setUser(cached);
        }
        setBooting(false);
        return;
      }
      const me = res.user as SessionUser;
      setCachedUser(me, mode);
      setUser(me);
      setBooting(false);
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } catch {
      // ignore network errors on logout
    }
    clearSession(mode);
    setUser(null);
  }, [mode]);

  return { booting, user, setUser, logout };
}

function AdminApp() {
  const { booting, user, setUser, logout } = useSessionBoot('admin');
  const location = useLocation();
  const navigate = useNavigate();
  const screen = adminScreenFromPath(location.pathname);

  const onAdminNav = useCallback(
    (key: AdminScreenKey) => {
      navigate(adminPathForScreen(key));
    },
    [navigate],
  );

  if (booting) {
    return (
      <div className="boot">
        <Loading label="جاري تجهيز التطبيق…" />
      </div>
    );
  }

  if (!user) {
    if (screen !== 'login') {
      return <Navigate to={`/${ADMIN_LOGIN_PATH}`} replace state={{ from: location.pathname }} />;
    }
    return (
      <AdminLoginScreen
        onSuccess={(u) => {
          setUser(u);
          const from = (location.state as { from?: string } | null)?.from;
          const fromScreen = from ? adminScreenFromPath(from) : null;
          const target =
            from && fromScreen && fromScreen !== 'login' ? from : `/${ADMIN_DEFAULT_PATH}`;
          navigate(target, { replace: true });
        }}
      />
    );
  }

  if (!screen || screen === 'login') {
    return <Navigate to={`/${ADMIN_DEFAULT_PATH}`} replace />;
  }

  return (
    <AppShell
      mode="admin"
      user={user}
      active={screen}
      onNavigate={(key) => onAdminNav(key as AdminScreenKey)}
      onLogout={() => {
        void logout().then(() => navigate(`/${ADMIN_LOGIN_PATH}`, { replace: true }));
      }}
    >
      {renderAdminScreen(screen, user, setUser, onAdminNav)}
    </AppShell>
  );
}

function CustomerApp() {
  const { booting, user, setUser, logout } = useSessionBoot('customer');
  const location = useLocation();
  const navigate = useNavigate();
  const screen = customerScreenFromPath(location.pathname);

  const onCustomerNav = useCallback(
    (key: CustomerScreenKey) => {
      navigate(customerPathForScreen(key));
    },
    [navigate],
  );

  if (booting) {
    return (
      <div className="boot">
        <Loading label="جاري تجهيز التطبيق…" />
      </div>
    );
  }

  if (!user) {
    if (screen === 'register') {
      return (
        <RegisterScreen
          onSuccess={(u) => {
            setUser(u);
            navigate(`/${CUSTOMER_DEFAULT_PATH}`, { replace: true });
          }}
          onLogin={() => navigate(`/${CUSTOMER_LOGIN_PATH}`)}
        />
      );
    }
    if (screen !== 'login') {
      return (
        <Navigate to={`/${CUSTOMER_LOGIN_PATH}`} replace state={{ from: location.pathname }} />
      );
    }
    return (
      <LoginScreen
        onSuccess={(u) => {
          setUser(u);
          const from = (location.state as { from?: string } | null)?.from;
          const fromScreen = from ? customerScreenFromPath(from) : null;
          const target =
            from && fromScreen && !isCustomerAuthScreen(fromScreen)
              ? from
              : `/${CUSTOMER_DEFAULT_PATH}`;
          navigate(target, { replace: true });
        }}
        onRegister={() => navigate(`/${CUSTOMER_REGISTER_PATH}`)}
      />
    );
  }

  if (!screen || isCustomerAuthScreen(screen)) {
    return <Navigate to={`/${CUSTOMER_DEFAULT_PATH}`} replace />;
  }

  return (
    <AppShell
      mode="customer"
      user={user}
      active={screen}
      onNavigate={(key) => onCustomerNav(key as CustomerScreenKey)}
      onLogout={() => {
        void logout().then(() => navigate(`/${CUSTOMER_LOGIN_PATH}`, { replace: true }));
      }}
      customerWallet={customerWalletMode(user.role, screen)}
    >
      {renderCustomerScreen(screen, user, setUser, onCustomerNav)}
    </AppShell>
  );
}

function renderCustomerScreen(
  key: CustomerScreenKey,
  user: SessionUser,
  setUser: (u: SessionUser) => void,
  onNavigate: (key: CustomerScreenKey) => void,
) {
  switch (key) {
    case 'home':
      return user.role === 'distributor' ? <DistributorScreen /> : <HomeScreen />;
    case 'bills':
      return <BillsScreen />;
    case 'products':
      return <ProductsScreen />;
    case 'wallet':
      return <WalletScreen />;
    case 'orders':
      return <OrdersScreen />;
    case 'more':
      return <MoreScreen user={user} onNavigate={onNavigate} />;
    case 'support':
      return <SupportScreen />;
    case 'kyc':
      return <KycScreen />;
    case 'notifications':
      return <NotificationsScreen />;
    case 'partnerApi':
      return <PartnerApiScreen />;
    case 'sessions':
      return <SessionsScreen />;
    case 'account':
      return <AccountScreen user={user} onUser={setUser} />;
    case 'quickpay':
      return (
        <QuickPayScreen
          user={user}
          onUserRefresh={() => {
            void auth.me().then((res) => {
              if (res.success && res.user && typeof res.user === 'object') setUser(res.user as SessionUser);
            });
          }}
        />
      );
    case 'distributor':
      return <DistributorScreen />;
    case 'customers':
      return <CustomersScreen />;
    case 'debts':
      return <DebtsScreen />;
    case 'reports':
      return <ReportsScreen />;
    case 'login':
    case 'register':
      return null;
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
}

function renderAdminScreen(
  key: AdminScreenKey,
  user: SessionUser,
  setUser: (u: SessionUser) => void,
  onNavigate: (key: AdminScreenKey) => void,
) {
  switch (key) {
    case 'dashboard':
      return <DashboardScreen onNavigate={onNavigate} user={user} />;
    case 'users':
      return <UsersScreen />;
    case 'deposits':
      return <DepositsScreen />;
    case 'payments':
      return <PaymentsScreen />;
    case 'units':
      return <UnitsOrdersScreen />;
    case 'games':
      return <GamesOrdersScreen />;
    case 'quickpay':
      return (
        <QuickPayScreen
          user={user}
          onUserRefresh={() => {
            void auth.me().then((res) => {
              if (res.success && res.user && typeof res.user === 'object') setUser(res.user as SessionUser);
            });
          }}
        />
      );
    case 'products':
      return <ProductsLocalScreen />;
    case 'categories':
      return <CategoriesScreen />;
    case 'ordering':
      return <OrderingScreen />;
    case 'import':
      return <ImportScreen />;
    case 'sources':
      return <SourcesScreen />;
    case 'recharge':
      return <RechargePackagesScreen />;
    case 'controls':
      return <ServiceControlsScreen />;
    case 'executor':
      return <ExecutorScreen />;
    case 'ussd':
      return <UssdScreen />;
    case 'sham':
      return <ShamBalanceScreen />;
    case 'sms':
      return <SmsMessagesScreen />;
    case 'bills':
      return <BillsAdminScreen />;
    case 'memberships':
      return <MembershipsScreen />;
    case 'site':
      return <SiteSettingsScreen />;
    case 'slides':
      return <SlidesScreen />;
    case 'password':
      return <AdminPasswordScreen />;
    case 'support':
      return <SupportScreen mode="admin" />;
    case 'notifications':
      return <NotificationsScreen mode="admin" />;
    case 'account':
      return <AccountScreen user={user} onUser={setUser} mode="admin" />;
    case 'financial':
      return <FinancialScreen />;
    case 'security':
      return <SecurityAuditScreen />;
    case 'compliance':
      return <ComplianceScreen />;
    case 'staff':
      return <StaffScreen />;
    case 'login':
      return null;
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
}

export default App;
